import { Injectable, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { first } from 'rxjs/operators';
import { SpeechService, VoiceCommand } from '../media/speech.service';
import { NavigationService } from '../core/navigation.service';
import { WaypointService } from '../core/waypoint.service';
import { AccountService } from '../account/account.service';
import { SettingsService } from '../account/settings.service';
import { StackedModalsService } from '../modal/stacked-modals.service';
import { RecognizedVoiceCommand } from '../../models/recognized-voice-command';
import { DisplayMode } from '../../models/display-mode';
import { NextTurn } from '../../models/route';
import { VoiceCommandConfirmationDialogComponent } from '../../components/modals/voice-command-confirmation-dialog/voice-command-confirmation-dialog.component';
import { VoiceCommandErrorDialogComponent } from '../../components/modals/voice-command-error-dialog/voice-command-error-dialog.component';
import { VoiceCommandListenDialogComponent } from '../../components/modals/voice-command-listen-dialog/voice-command-listen-dialog.component';

@Injectable({ providedIn: 'root' })
export class VoiceCommandFlowHandler {
  private commandSub: Subscription | null = null;
  private listenSub: Subscription | null = null;
  private navSub: Subscription | null = null;
  private isNavigating = false;

  private readonly speechService = inject(SpeechService);
  private readonly navigationService = inject(NavigationService);
  private readonly waypointService = inject(WaypointService);
  private readonly accountService = inject(AccountService);
  private readonly settingsService = inject(SettingsService);
  private readonly stackedModalsService = inject(StackedModalsService);

  bind(): void {
    if (!this.navSub) {
      this.navSub = this.navigationService.ObservableChangeNavigation.subscribe(resp => {
        if (resp === 'start') this.isNavigating = true;
        else if (resp === 'exit' || resp === 'arrive') this.isNavigating = false;
      });
    }
    if (!this.commandSub) {
      this.commandSub = this.speechService.ObservableRecognizedVoiceCommand.subscribe(
        (command: RecognizedVoiceCommand | null) => {
          if (command != null) this.handleCommand(command);
        },
      );
    }
    if (!this.listenSub) {
      this.listenSub = this.speechService.ObservableOpenVoiceCommandListen.subscribe(resp => {
        if (resp) {
          this.stackedModalsService.openModal(VoiceCommandListenDialogComponent, { disableClose: true });
        }
      });
    }
  }

  unbind(): void {
    this.commandSub?.unsubscribe();
    this.commandSub = null;
    this.listenSub?.unsubscribe();
    this.listenSub = null;
    this.navSub?.unsubscribe();
    this.navSub = null;
  }

  private handleCommand(command: RecognizedVoiceCommand): void {
    switch (command.CommadType) {
      case VoiceCommand.SetBattery:
        this.openConfirmation(command, { batteryValue: command.Value });
        break;
      case VoiceCommand.SkipWP:
        if (this.waypointService.getSelectedDisplayMode() !== DisplayMode.RANGE && this.waypointService.getWaypointsParams().length > 2) {
          this.openConfirmation(command);
        } else {
          this.openNotAvailableError();
        }
        break;
      case VoiceCommand.SkipCS:
      case VoiceCommand.StopNavigation:
        this.confirmDuringNavigation(command, () => ({}));
        break;
      case VoiceCommand.SetWeight:
        this.openConfirmation(command, { weightValue: command.Value });
        break;
      case VoiceCommand.NavigateToHome: {
        const homeAddress = this.settingsService.getHomeAddress();
        this.confirmSavedAddress(command, homeAddress?.Description, { homeAddress: homeAddress });
        break;
      }
      case VoiceCommand.NavigateToWork: {
        const workAddress = this.settingsService.getWorkAddress();
        this.confirmSavedAddress(command, workAddress?.Description, { workAddress: workAddress });
        break;
      }
      case VoiceCommand.ReadEta:
        this.confirmDuringNavigation(command, () => ({ Eta: this.readNextTurn(resp => this.etaFromNow(resp.TimeToDest)) }));
        break;
      case VoiceCommand.ReadEte:
        this.confirmDuringNavigation(command, () => ({ Ete: this.readNextTurn(resp => resp.TimeToDest) }));
        break;
      case VoiceCommand.ReadRemainingDist:
        this.confirmDuringNavigation(command, () => ({ RemainingDist: this.readNextTurn(resp => resp.DistToDest) }));
        break;
    }
  }

  private openConfirmation(command: RecognizedVoiceCommand, extraData: object = {}): void {
    this.stackedModalsService.openModal(VoiceCommandConfirmationDialogComponent,
      { data: { commandType: command.CommadType, ...extraData }, autoFocus: false });
  }

  private openNotAvailableError(): void {
    this.stackedModalsService.openModal(VoiceCommandErrorDialogComponent,
      { data: { errorMsg: "Function is not available." } });
    this.speechService.PlayErrorNotAvailable();
  }

  // Commands that only make sense while driving. The extra dialog data is built
  // only once navigation is confirmed, since it may read the current next turn.
  private confirmDuringNavigation(command: RecognizedVoiceCommand, extraData: () => object): void {
    if (this.isNavigating) {
      this.openConfirmation(command, extraData());
    } else {
      this.stackedModalsService.openModal(VoiceCommandErrorDialogComponent,
        { data: { errorMsg: "This function is only available during navigation." } });
      this.speechService.PlayErrorAvailableDuringNavigation();
    }
  }

  private confirmSavedAddress(command: RecognizedVoiceCommand, description: string | undefined, extraData: object): void {
    if (this.accountService.getIsAuthorized() && description) {
      this.openConfirmation(command, extraData);
    } else {
      this.openNotAvailableError();
    }
  }

  // Synchronously reads the current next-turn value (a BehaviorSubject);
  // undefined when there is none yet.
  private readNextTurn<T>(pick: (turn: NextTurn) => T): T | undefined {
    let value: T | undefined;
    this.navigationService.ObservableNextTurn.pipe(first()).subscribe((resp) => {
      if (resp) {
        value = pick(resp);
      }
    });
    return value;
  }

  // Arrival time as minutes past midnight, wrapped to the next day.
  private etaFromNow(timeToDest: number): number {
    const date = new Date();
    const dateInMinute = date.getHours() * 60 + date.getMinutes();
    let eta = dateInMinute + timeToDest;
    if (eta > 60 * 24) {
      eta -= 60 * 24;
    }
    return eta;
  }
}
